import React, { FC } from 'react';
import { FontColor, Headline2, Headline3, Headline5, TextDefault } from '../../Text';
import { Icon } from '../../Icon';
import { Flexbox, FlexboxCenter, FlexboxVertical } from '../../Layout';
import styled from 'styled-components';
import { Colors } from '../../../../styles/colors';
import { Product } from '../../../../interfaces/Product';
import { PanelWithGradient } from '../../PanelWithGradient';
import { parseProductColorSet } from '../icons/ProductIconWrapper';
import { useAttachmentDataUrl } from '../../../../utils/Attachments';
import { AnimatedContainer } from '../../AnimatedContainer';

const StageWrapper = styled(Flexbox)`
  align-items: stretch;

  & > * {
    flex: 1 0 0;

    &:first-child {
      flex: 2 0 0;
    }
  }
  
  ${PanelWithGradient} {
    li {
      margin-left: 1rem;
      margin-bottom: .5rem;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const ChevronButtonWrapper = styled(FlexboxVertical)<{ top: boolean }>`
  height: 3rem;
  justify-content: ${({ top }) => top ? 'flex-start' : 'flex-end'};  
`;
const ScrollDownButtonWrapper = styled(FlexboxCenter)`
  height: 6rem;
  color: ${Colors.tertiary};
  cursor: pointer;
  
  transition: transform 300ms ease-out;
  transform: none;
  
  &:hover {
    transform: translateY(.67rem);
  }  
  
  ${ChevronButtonWrapper} {
    position: absolute;
    transform: translateY(-.5rem);
    
    &:last-child {
      transform: translateY(.5rem);
    }
  }
`;

const LeftPanel = styled(FlexboxVertical)`
  z-index: 1;

  ${Headline2} {
    margin-bottom: 3.5rem;
  }
  ${Headline3} {
    margin-bottom: 2.5rem;
  }
  ${PanelWithGradient} {
    padding: 2.5rem 3rem 2rem 7rem;
    margin-left: -7rem;
    min-height: 14rem;
  }
`;

const LogoWrapper = styled(Flexbox)`
  padding: .5rem 3rem 2.5rem;
`;
const EcoLogoImage = styled.img`
  width: 12.5rem;
  height: auto;
  object-fit: contain;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CenterContentWrapper = styled(FlexboxVertical)`
  align-self: center;
  align-items: flex-start;
  margin: 0 auto;
`;

const RightPanel = styled(FlexboxVertical)`
  z-index: 0;
  
  ${FlexboxVertical} {
    padding: 1rem 3rem;
    align-items: flex-start;
    text-align: left;
  }
  
  ${Image} {
    width: calc(100% + 12rem);
    height: 18rem;
    margin-top: auto;
    margin-left: -6rem;
    margin-right: -6rem;
  }
`;

const TargetGroupWrapper = styled(Flexbox)`
  align-items: flex-end;
  padding-bottom: .5rem;
  
  ${Headline5} {
    margin-left: .5rem;
    line-height: .75;
  }
`;

interface Props {
  product?: Product;
}

export const ProductDetailStage: FC<Props> = ({ product, ...props }) => {
  const accentColor = parseProductColorSet(product?.metaProduct?.colorSet);
  const imageUrl = useAttachmentDataUrl(product?.metaProduct?.stage?.stageImageId);

  return (
    <StageWrapper>

      <LeftPanel>
        <FontColor color={accentColor}><Headline2 dangerouslySetInnerHTML={{ __html: product?.metaProduct?.stage?.fullName }} /></FontColor>

        <Headline3 dangerouslySetInnerHTML={{ __html: product?.description }} />

        <PanelWithGradient product={product}>
          <ul>
            {
              (product?.metaProduct?.stage?.keyFeatures || []).map((item) => (<li dangerouslySetInnerHTML={{ __html: item }} />))
            }
          </ul>
        </PanelWithGradient>

        <ScrollDownButtonWrapper
          onClick={() => {
            const viewport = document.querySelector('main.app');
            viewport?.scrollTo({
              top: viewport?.scrollHeight || 0,
              left: 0,
              behavior: 'smooth',
            });
          }}
        >
          <ChevronButtonWrapper top={true}>
            <Icon icon={'chevron'} size={48} />
          </ChevronButtonWrapper>
          <ChevronButtonWrapper top={false}>
            <Icon icon={'chevron'} size={48} />
          </ChevronButtonWrapper>
        </ScrollDownButtonWrapper>
      </LeftPanel>

      <RightPanel>
        <CenterContentWrapper>
          <FlexboxVertical>
            <TargetGroupWrapper>
              <Icon icon={'target-group'} size={24} />
              <Headline5>Zielgruppe</Headline5>
            </TargetGroupWrapper>
            <TextDefault dangerouslySetInnerHTML={{ __html: product?.metaProduct?.stage?.targetGroupsHint }} />
          </FlexboxVertical>

          {
            product?.eco &&
            <AnimatedContainer active={true} delay={500}>
              <LogoWrapper>
                <EcoLogoImage alt='' src={product?.type === 'ERDGAS' ? './assets/images/oekoerdgas.svg' : './assets/images/oekostrom.svg'} />
              </LogoWrapper>
            </AnimatedContainer>
          }
        </CenterContentWrapper>

        <Image alt='' src={imageUrl} />
      </RightPanel>

    </StageWrapper>
  );
};
